/**
 * worldmapFigure
 *
 * @since 2018-03-12
 * @author ms1124.kim
 * @version 0.0.2
 *
 * 수정 이력
 *
 * - 2018.03.12 김민수 : 외부 모듈 최초 정의
 * - 2018.03.20 김민수 : 기능별 주석 작성
 * - 2018.03.21 김민수 : 세계지도 중앙좌표 계산코드 개선
 * - 2018.03.22 김민수 : 세계지도 리사이징 기능 변경
 * - 2018.06.09 김민수 : D3 tip SVG 방식으로 변경
 *
 * 기능 개선 예정
 *
 * - 위도, 경도 지원 : 국가 코드가 아닌 위도, 경도의 값으로 요소가 그려지는 기능 추가 예정
 * - 지도 스타일 : 지도의 스타일이 지원되도록 수정
 */

import * as d3 from 'd3'
import * as geoJSON from '~/static/worldmap/worldmap.json'
// TAMS
import 'd3-zoom'

const defaultJSON = geoJSON.default

const d3Figure = (function () {
  'use strict'

  /*
  Object - d3Figure

  d3Figure 플러그인에서 실제 기능을 제공하는 객체입니다. 이 객체는 다음과 같이 구성됩니다.

  - svg : 생성된 SVG 태그를 저장
  - mapWidth : SVG 컨테이너 너비
  - mapHeight: SVG 컨테이너 높이
  - geoPath : geoPATH 데이터
  - geoProjection : d3-geo 투영 객체. geoEquirectangle 이 기본
  - features: geoJSON features 객체
  - defaultOptions: d3Figure 객체에서 제공하는 요소들의 기본 옵션
   */
  const d3Figure = {}
  /*
  Method - initWorldMap

  세계지도를 화면에 출력하기 위한 기본 설정을 수행합니다. 이 단계에서는 SVG를 부모 컨테이너의 크기에 맞게 생성하고, D3 템플릿을 정의하며, 세계 지도를
  렌더링 합니다.
   */
  d3Figure.initWorldMap = function (vueInstance, containerWidth, containerHeight, options = {}) {
    const targetContainer = vueInstance.$el

    // 옵션 저장
    vueInstance.$data.options = options
    // 부모 영역 너비, 높이 멤버변수에 저장
    vueInstance.$data.mapWidth = containerWidth
    vueInstance.$data.mapHeight = containerHeight
    // 코드 포멧 추가
    vueInstance.$data.format = options.codeFormat || 'iso_a2'
    vueInstance.$data.features = defaultJSON.features

    // TAMS
    // Zoom 기능 추가
    const zoom = d3.zoom()
      .extent([[0, 0], [vueInstance.$data.mapWidth, vueInstance.$data.mapHeight]])
      .translateExtent([[0, 0], [vueInstance.$data.mapWidth, vueInstance.$data.mapHeight]])
      .scaleExtent([1, 10])
      .on('zoom', () => {
        // const self = this
        // this.zoomed(self)

        const width = vueInstance.$data.mapWidth
        const height = vueInstance.$data.mapHeight
        const t = [d3.event.transform.x, d3.event.transform.y]
        const s = d3.event.transform.k

        const wMax = 0
        const wMin = width * (1 - s)
        const hMax = height < s * width / 2 ? s * (width / 2 - height) / 2 : (1 - s) * height / 2
        const hMin = height < s * width / 2 ? -s * (width / 2 - height) / 2 - (s - 1) * height : (1 - s) * height / 2

        t[0] = Math.min(wMax, Math.max(wMin, t[0]))
        t[1] = Math.min(hMax, Math.max(hMin, t[1]))

        // this.zoom.translate(t)
        vueInstance.$data.g.attr('transform', `translate(${t})scale(${s})`)
        vueInstance.$data.g.selectAll('path').style('stroke-width', 0.5 / s + 'px')
      })

    // SVG 컨테이너 추가
    const svg = d3.select(targetContainer)
      .append('svg')
      .style('width', containerWidth + 'px')
      .style('height', containerHeight + 'px')
      // TAMS
      // .call(zoom) // TAMS 임시 제외 (zoom기능 콜 부분)
    const g = svg.append('g') // zoom을 위한 상위 그룹 등록

    // 내부 멤버에 추가
    vueInstance.$data.svg = svg

    // TAMS
    vueInstance.$data.g = g
    vueInstance.$data.zoom = zoom

    // 세계지도 Path 그룹
    // svg.append('g')
    g.append('g') // TAMS
      .attr('itemid', 'g_path_worldmap')

    // 도형 그룹
    // svg.append('g')
    g.append('g') // TAMS
      .attr('itemid', 'g_path_figure')

    // 템플릿 그룹
    // const groupTemplate = svg.append('g')
    const groupTemplate = g.append('g') // TAMS
      .attr('itemid', 'g_defs_template')

    // Defs 템플릿 - 필터추가
    const templateEl = groupTemplate.append('defs')
    this.initTemplate(templateEl)

    // 세계지도 그리기
    this.drawWorldMap(vueInstance, options)
  }

  /*
  Method - destroyWorldMap

  세계지도 객체를 해제합니다.
  */
  d3Figure.destroyWorldMap = function (vueInstance) {
    // 뷰 인스턴스 DOM 가져오기
    const targetContainer = vueInstance.$el

    // SVG 컨테이너 가져오기
    const svg = d3.select(targetContainer)

    // 내부 모델 제거
    delete vueInstance.$data.options
    delete vueInstance.$data.features

    // 이벤트 제거
    const selectAll = svg.selectAll('*')

    selectAll.on('click', null)
    selectAll.on('mouseout', null)
    selectAll.on('mouseover', null)
    selectAll.on('mousemove', null)

    // SVG 하위 메모리 해제
    selectAll.remove()

    // SVG 제거
    svg.remove()

    // SVG 내부모델 제거
    delete vueInstance.$data.svg
  }

  /*
  Method - initTemplate

  모든 요소들이 공통으로 사용하는 템플릿을 정의합니다.
  */
  d3Figure.initTemplate = function (templateEl) {
    // 그림자 필터 추가
    const filterShadow = templateEl.append('filter')
      .attr('id', 'template-dropshadow')
      .attr('height', '120%')
    filterShadow.append('feGaussianBlur')
      .attr('in', 'SourceAlpha')
      .attr('stdDeviation', 1)
      .attr('result', 'blur')
    filterShadow.append('feOffset')
      .attr('in', 'blur')
      .attr('dx', 2)
      .attr('dy', 2)
      .attr('result', 'offsetBlur')

    const filterMarge = filterShadow.append('feMerge')
    filterMarge.append('feMergeNode')
      .attr('in', 'offsetBlur')
    filterMarge.append('feMergeNode')
      .attr('in', 'SourceGraphic')

    // 그라디언트 추가 - 제거
    // const gradient = templateEl.append('linearGradient')
    //   .attr('id', 'template-worldmap-gradient')
    //   .attr('x1', '0%')
    //   .attr('y1', '0%')
    //   .attr('x2', '0%')
    //   .attr('y2', '100%')
    //   .attr('spreadMethod', 'pad')
    // gradient.append('stop')
    //   .attr('offset', '0%')
    //   .attr('stop-color', '#0F3871')
    //   .attr('stop-opacity', 1)
    // gradient.append('stop')
    //   .attr('offset', '100%')
    //   .attr('stop-color', '#498dda')
    //   .attr('stop-opacity', 1)
  }
  /*
  Method - redrawWorldMap

  브라우저가 Resize되면 세계지도 컴포넌트를 다시 렌더링합니다.
   */
  d3Figure.redrawWorldMap = function (vueInstance, width, height, redrawObject) {
    // SVG 엘리먼트 날리기
    vueInstance.$data.svg.select('[itemid=g_path_figure]').selectAll('*').remove()
    // SVG 크기 변경
    vueInstance.$data.svg.style('width', width + 'px')
    vueInstance.$data.svg.style('height', height + 'px')

    const newProjection = vueInstance.$data.geoProjection
      .fitSize([width, height], defaultJSON)
    // .translate([width / 2, height / 2])

    const newPath = d3.geoPath(newProjection)
    vueInstance.$data.svg.select('[itemid=g_path_worldmap]')
      .selectAll('path')
      .attr('d', newPath)
    // Redraw 대상 요소 다시 그리기
    // 0. 텍스트
    for (const redrawEl of redrawObject.text) {
      this.drawText(vueInstance, redrawEl.target, redrawEl.options)
    }
    // 1. 이미지
    for (const redrawEl of redrawObject.image) {
      this.drawImage(vueInstance, redrawEl.target, redrawEl.path, redrawEl.options)
    }
    // 2. 도형
    for (const redrawEl of redrawObject.circle) {
      this.drawCircle(vueInstance, redrawEl.target, redrawEl.options)
    }
    // 3. 라인
    for (const redrawEl of redrawObject.line) {
      this.drawArrowLine(vueInstance, redrawEl.src, redrawEl.dst, redrawEl.options)
    }
  }
  /*
  Method - removeFigureElement

  파라미터로 전달된 itemId을 가지는 엘리먼트를 찾아 제거합니다. 파라미터가 없으면 세계지도를 제외한 모든 요소를 제거합니다.
   */
  d3Figure.removeFigureElement = function (vueInstance, targetId = 'all') {
    const figureGroup = vueInstance.$data.svg.select('[itemid=g_path_figure]')

    if (figureGroup.empty()) {
      console.error('No Worldmap geometry elements added.') // 추가된 Worldmap 도형 요소가 없습니다.
      return
    }

    if (targetId === 'all') {
      figureGroup.selectAll('*').remove()
    } else {
      const targetEl = vueInstance.svg.select(`[itemid=${targetId}]`)

      if (targetEl.empty()) {
        console.error('No element with passed Item ID attribute.') // 전달된 Item ID 속성을 가진 요소가 없습니다.

        return
      }

      targetEl.remove()
    }
  }
  /*
  Method - changeWorldmapColor

  사용자가 옵션으로 전달한 colorMappingByValue가 있을경우, 이 메서드를 기준으로 지도의 색상을 변경합니다.
  */
  d3Figure.changeWorldmapColor = function (vueInstance, changeColorFunc) {
    const instanceModel = vueInstance.$data

    if (changeColorFunc || instanceModel.options.colorMappingByValue) {
      // SVG에서 지도의 Path 그룹을 가져온다.
      const group = instanceModel.svg.select('[itemid=g_path_worldmap]')

      // 색상을 변경한다.
      group.selectAll('path').style('fill', changeColorFunc === null ? instanceModel.options.colorMappingByValue : changeColorFunc)
    } else {
      // 경고 콘솔 메세지를 띄우고 함수를 종료한다.
      console.warn('There is no color mapping method. This function request is ignored.')
    }
  }
  /*
  Method - drawWorldMap

   SVG 태그에 세계지도를 렌더링합니다. 투영법은 geoEquirectangular를 사용합니다.
  */
  d3Figure.drawWorldMap = function (vueInstance, options) {
    const group = vueInstance.$data.svg.select('[itemid=g_path_worldmap]')
    const width = vueInstance.$data.mapWidth
    const height = vueInstance.$data.mapHeight

    // 지도 투영법 객체 생성
    vueInstance.$data.geoProjection = d3.geoEquirectangular()
      .rotate([-162, 0])
      .fitSize([width, height], defaultJSON)

    // Geo Path 객체 저장
    vueInstance.$data.geoPath = d3.geoPath()
      .projection(vueInstance.$data.geoProjection)

    const worldmap = group.selectAll('path')
      .data(vueInstance.$data.features)
      .enter()
      .append('path')
      .attr('d', vueInstance.$data.geoPath)
      .attr('class', 'worldmap-boundary')
      .attr('itemid', (data) => 'path_country_' + data.properties.iso_a2)
      .style('fill', options.colorMappingByValue)
    // .attr('class', (geoData) => `worldmap-continent-${geoData.properties.continent.toLowerCase().replace(' ', '')} worldmap-boundary`)

    // 월드맵 마우스 이벤트 추가
    if (options.click) {
      worldmap.on('click', (data) => {
        // 국가 코드 포맷에 따라 저장
        const countryCode = data.properties[vueInstance.$data.format]

        // Path 엘리먼트 저장
        const self = vueInstance.$data.svg.select(`[itemid=path_country_${countryCode}]`)

        // 센터 좌표 구하기
        const centerCoordinate = this.getCenterCoordinates(vueInstance, countryCode)

        // D3 Event 저장
        const event = d3.event

        // D3 지도 데이터에 파라미터 설정
        data.coordinate = centerCoordinate

        // 현재 클릭된 정보를 메모리에 저장
        vueInstance.$data.innerMemory.worldmap.beforeSelected = {
          self,
          data,
          event
        }

        // 지도 클릭 이벤트 연결
        options.click(self, event, data)
      })
    }

    if (options.mouseout) {
      worldmap.on('mouseout', (data) => {
        // 국가 코드 포맷에 따라 저장
        const countryCode = data.properties[vueInstance.$data.format]
        const self = vueInstance.$data.svg.select(`[itemid=path_country_${countryCode}]`)
        // 센터 좌표 구하기
        const centerCoordinate = this.getCenterCoordinates(vueInstance, countryCode)
        // D3 Event 저장
        const event = d3.event
        // D3 지도 데이터에 파라미터 설정
        data.coordinate = centerCoordinate
        options.mouseout(self, event, data)
      })
    }

    if (options.mouseover) {
      worldmap.on('mouseover', (data) => {
        // 국가 코드 포맷에 따라 저장
        const countryCode = data.properties[vueInstance.$data.format]
        const self = vueInstance.$data.svg.select(`[itemid=path_country_${countryCode}]`)
        // 센터 좌표 구하기
        const centerCoordinate = this.getCenterCoordinates(vueInstance, countryCode)
        // D3 Event 저장
        const event = d3.event
        // D3 지도 데이터에 파라미터 설정
        data.coordinate = centerCoordinate
        options.mouseover(self, event, data)
      })
    }

    if (options.mousemove) {
      worldmap.on('mousemove', (data) => {
        // 국가 코드 포맷에 따라 저장
        const countryCode = data.properties[vueInstance.$data.format]
        const self = vueInstance.$data.svg.select(`[itemid=path_country_${countryCode}]`)
        // 센터 좌표 구하기
        const centerCoordinate = this.getCenterCoordinates(vueInstance, countryCode)
        // D3 Event 저장
        const event = d3.event
        // D3 지도 데이터에 파라미터 설정
        data.coordinate = centerCoordinate
        options.mousemove(self, event, data)
      })
    }
  }
  /*
  Method - getCountryCoordinates

  전달된 국가코드에 해당하는 데이터를 geoJSON에서 탐색하여 리턴합니다. type에 따라 2자리 / 3자리의 국가코드를 지원합니다. (iso_a2, iso_a3)
   */
  d3Figure.getCountryCoordinates = function (vueInstance, countryCode) {
    const arrFeatures = defaultJSON.features
    const upperCode = countryCode.toUpperCase()
    for (const geoData of arrFeatures) {
      if (geoData.properties[vueInstance.$data.format || 'iso_a2'] === upperCode) {
        return geoData
      }
    }
  }
  /*
  Method - getGeoBounds

  파라미터로 전달된 국가코드에 해당하는 데이터를 geoJSON에서 탐색하고, 세계지도의 bounds 객체를 리턴합니다.
   */
  d3Figure.getGeoBounds = function (vueInstance, countryCode) {
    const targetBound = this.getCountryCoordinates(vueInstance, countryCode, vueInstance.$data.format || 'iso_a2')
    return vueInstance.$data.geoPath.bounds(targetBound)
  }
  /*
  Method - getLonglatiCoordinates

  파라미터로 전달된 좌표를 SVG의 맵의 좌표로 변환하여 리턴합니다.
   */
  // d3Figure.getLonglatiCoordinates = function (arrCoordinates) {
  //   return this.geoPath({
  //     type: 'LineString ',
  //     coordinates: [
  //       arrCoordinates
  //     ]
  //   })
  // }
  /*
  Method - getCenterCoordinates

  파라미터로 전달된 국가코드로 세계지도에서 국가의 좌표를 구하고, 정중앙의 좌표를 리턴합니다.
   */
  d3Figure.getCenterCoordinates = function (vueInstance, countryCode) {
    const bounds = this.getGeoBounds(vueInstance, countryCode)
    const x1 = bounds[0][0], y1 = bounds[0][1], x2 = bounds[1][0], y2 = bounds[1][1]
    const centerX = x1 + ((x2 - x1) / 2), centerY = y1 + ((y2 - y1) / 2)

    return {sx: x1, sy: y1, ex: x2, ey: y2, cx: centerX, cy: centerY}
  }
  /*
  Method - drawArrowLine

  Source 국가부터 Destination 국가까지 화살표 곡선을 렌더링 합니다.
   */
  d3Figure.drawArrowLine = function (vueInstance, source, destination, options = {}) {
    const group = vueInstance.$data.svg.select('[itemid=g_path_figure]')

    // geoPath 사각형 영역 구하기
    const srcCenter = this.getCenterCoordinates(vueInstance, source)
    const dstCenter = this.getCenterCoordinates(vueInstance, destination)

    // 사용자 옵션 선언
    const lineOptions = {...vueInstance.$data.defaultOptions.line, ...options}
    const markerOptions = {...vueInstance.$data.defaultOptions.marker}

    // Source, Mid (곡선을 그릴 기준), Destination 구하기
    const srcX = srcCenter.cx + lineOptions.srcOffsetX, srcY = srcCenter.cy + lineOptions.srcOffsetY
    const dstX = dstCenter.cx + lineOptions.dstOffsetX, dstY = dstCenter.cy + lineOptions.dstOffsetY
    const midX = (srcX > dstX) ? (srcX - dstX) / 2 : (dstX - srcX) / 2, midY = (srcY > dstY) ? (srcY - dstY) / 2 : (dstY - srcY) / 2

    let midCoordinate

    if (midX > midY) {
      midCoordinate = `${srcX + midX} ${dstY - 100}`
    } else {
      midCoordinate = `${srcX + 100}  ${srcY + midY - 200}`
    }

    // 중복으로 그려지지 않도록 ID 생성 - 국가코드로 생성
    const lineItemid = `line_s${source.toLowerCase()}d_${destination.toLowerCase()}`

    // 중복되는 요소가 있는지 확인
    if (vueInstance.$data.svg.select(`[itemid=${lineItemid}]`).empty()) {
      let lineGroup = group.select('[itemid=g_figure_line]')

      if (lineGroup.empty()) {
        vueInstance.$data.svg.select('[itemid=g_path_figure]').append('g')
          .attr('itemid', 'g_figure_line')
      }

      // 라인별 그룹 생성
      const newLine = vueInstance.$data.svg.select('[itemid=g_figure_line]').append('g')

      // 커스텀 마커 생성
      const templateEl = newLine.append('defs')
      const defaultType = markerOptions.type

      templateEl.append('marker')
        .attr('id', `line_marker_start_${lineItemid}`)
        .attr('viewBox', markerOptions[defaultType].viewBox.start)
        .attr('refX', markerOptions[defaultType].refX.start)
        .attr('refY', markerOptions[defaultType].refY.start)
        .attr('markerWidth', markerOptions[defaultType].markerWidth)
        .attr('markerHeight', markerOptions[defaultType].markerHeight)
        .attr('orient', markerOptions[defaultType].orient)
        .style('fill', lineOptions.stroke)
        .append('path')
        .attr('d', markerOptions.arrow.d.start)

      templateEl.append('marker')
        .attr('id', `line_marker_end_${lineItemid}`)
        .attr('viewBox', markerOptions[defaultType].viewBox.end)
        .attr('refX', markerOptions[defaultType].refX.end)
        .attr('refY', markerOptions[defaultType].refY.end)
        .attr('markerWidth', markerOptions[defaultType].markerWidth)
        .attr('markerHeight', markerOptions[defaultType].markerHeight)
        .attr('orient', markerOptions[defaultType].orient)
        .style('fill', lineOptions.stroke)
        .append('path')
        .attr('d', markerOptions.arrow.d.end)

      // Group 요소에 라인추가
      newLine.append('path')
        .attr('itemid', lineItemid)
        .attr('d', `M${srcX} ${srcY} Q ${midCoordinate} ${dstX} ${dstY}`)
        .style('fill', 'none')
        .style('filter', 'url(#template-dropshadow)')
        .style('stroke', lineOptions.stroke)
        .style('stroke-width', lineOptions['stroke-width'])
        .attr('marker-start', (lineOptions.direction === 'oneway') ? '' : `url(#line_marker_start_${lineItemid})`)
        .attr('marker-end', `url(#line_marker_end_${lineItemid})`)

      // Line 마우스 이벤트 추가
      if (lineOptions.click) {
        newLine.on('click', (data) => {
          const event = d3.event
          lineOptions.click(event, data)
        })
      }

      if (lineOptions.mouseout) {
        newLine.on('mouseout', (data) => {
          const event = d3.event
          lineOptions.mouseout(event, data)
        })
      }

      if (lineOptions.mouseover) {
        newLine.on('mouseover', (data) => {
          const event = d3.event
          lineOptions.mouseover(event, data)
        })
      }

      if (lineOptions.mousemove) {
        newLine.on('mousemove', (data) => {
          const event = d3.event
          lineOptions.mousemove(event, data)
        })
      }

      return lineItemid
    }
  }
  /*
  Method - drawText

  전달된 target의 국가 좌표에 문자열을 렌더링 합니다. 마지막 인자에 요소의 Itemid 속성값을 전달하면, 해당 요소의 데이터 / 속성이 업데이트 됩니다.
   */
  d3Figure.drawText = function (vueInstance, target, options = {}, textItemId = '') {
    // 국가코드로 센터좌표 가져오기
    const center = this.getCenterCoordinates(vueInstance, target)

    // Text 그룹이 있는지 확인
    let textGroup = vueInstance.$data.svg.select('[itemid=g_path_figure]')
      .select('[itemid=g_figure_text]')

    // Text 그룹이 없으면, 그룹생성
    if (textGroup.empty()) {
      textGroup = vueInstance.$data.svg.select('[itemid=g_path_figure]').append('g')
        .attr('itemid', 'g_figure_text')
    }

    // 새롭게 추가하는 요소인지, 업데이트 할 요소인지 확인
    if (textItemId === '') {
      // Text 요소의 옵션 정의
      const textOptions = {...vueInstance.$data.defaultOptions.text, ...options}

      // 그룹에 추가되어있는 Circle 갯수 확인
      const textCount = textGroup.selectAll('g').size()

      // text 요소 이름속성 만들기
      const newTextItemId = `text_itemid_${target.toLowerCase()}_${textCount}`

      // Text 그룹 생성
      const newTextGroup = textGroup.append('g')

      // Text 요소 추가
      const newText = newTextGroup.append('text')
        .attr('itemid', newTextItemId)
        .attr('x', center.cx + textOptions.offsetX)
        .attr('y', center.cy + textOptions.offsetY)
        .attr('dx', textOptions.dx)
        .attr('dy', textOptions.dy)
        .attr('class', textOptions.class)
        .style('text-anchor', textOptions['text-anchor'])
        .style('font-weight', textOptions['font-weight'])
        .style('font-size', textOptions['font-size'])
        .style('fill', textOptions.fill)
        .text(textOptions.text)

      // Text 마우스 이벤트 추가
      if (textOptions.click) {
        newText.on('click', (data) => {
          const event = d3.event
          textOptions.click(event, data)
        })
      }

      if (textOptions.mouseout) {
        newText.on('mouseout', (data) => {
          const event = d3.event
          textOptions.mouseout(event, data)
        })
      }

      if (textOptions.mouseover) {
        newText.on('mouseover', (data) => {
          const event = d3.event
          textOptions.mouseover(event, data)
        })
      }

      if (textOptions.mousemove) {
        newText.on('mousemove', (data) => {
          const event = d3.event
          textOptions.mousemove(event, data)
        })
      }

      // ItemId 리턴
      return newTextItemId
    } else {
      // 변경하고자 하는 문자의 itemid가  전달 되었다면 속성 변경
      const targetText = textGroup.select(`[itemid=${textItemId}]`)

      // 대상 문자가 있는지 확인
      if (targetText.empty()) {
        console.error('No string element with passed itemid attribute.') // 전달된 itemid 속성을 가지는 문자열 요소가 없습니다.

        return
      }

      // 문자열 속성 변경
      targetText.attr('x', Number((!options.offsetX) ? targetText.attr('offsetX') + center.cx : options.offsetX + center.cx))
        .attr('y', Number((!options.offsetY) ? targetText.attr('offsetY') + center.cy : options.offsetY + center.cy))
        .attr('dx', (!options.dx) ? targetText.attr('dx') : options.dx)
        .attr('dy', (!options.dy) ? targetText.attr('dy') : options.dy)
        .style('font-size', (!options['font-size']) ? targetText.style('font-size') : options['font-size'])
        .style('font-weight', (!options['font-weight']) ? targetText.style('font-weight') : options['font-weight'])
        .style('text-anchor', (!options['text-anchor']) ? targetText.style('text-anchor') : options['text-anchor'])
        .style('fill', (!options.fill) ? targetText.style('fill') : options.fill)
        .text((!options.text) ? targetText.text() : options.text)

      // ItemId 리턴
      return textItemId
    }
  }
  /*
  Method - drawCircle

  전달된 target의 좌표에 원형 도형을 렌더링 합니다. 원형 도형 안에는 하나의 Text 요소가 있으며, 원의 크기에 따라 문자열의 크기가 자동으로 조절됩니다.
   */
  d3Figure.drawCircle = function (vueInstance, target, options = {}, circleItemId = '') {
    // 국가코드로 센터좌표 가져오기
    const center = this.getCenterCoordinates(vueInstance, target)

    // Circle 그룹이 있는지 확인
    const circleGroup = vueInstance.$data.svg.select('[itemid=g_path_figure]')
      .select('[itemid=g_figure_circle]')

    // Circle 그룹이 없으면, 그룹생성
    if (circleGroup.empty()) {
      vueInstance.$data.svg.select('[itemid=g_path_figure]').append('g')
        .attr('itemid', 'g_figure_circle')
    }

    // 새로운 도형을 그리는 건지, 업데이트인지 판단
    if (circleItemId === '') {
      // Circle 요소의 옵션 정의
      const circleOptions = {...vueInstance.$data.defaultOptions.circle, ...options}

      // 그룹에 추가되어있는 Circle 갯수 확인
      const circleSize = circleGroup.selectAll('g').size()

      // Circle 요소 이름속성 만들기
      const newCircleItemId = `circle_itemid_${target.toLowerCase()}_${circleSize}`

      // Circle 그룹 추가
      const newCircleGroup = vueInstance.$data.svg.select('[itemid=g_figure_circle]')
        .append('g')

      // Circle 그룹에 circle 추가
      const newCircle = newCircleGroup.append('circle')

      // Circle 추가
      newCircle.attr('itemid', newCircleItemId)
        .attr('cx', center.cx + circleOptions.offsetX)
        .attr('cy', center.cy + circleOptions.offsetY)
        .attr('offsetX', circleOptions.offsetX)
        .attr('offsetY', circleOptions.offsetY)
        .attr('r', circleOptions.size)
        .style('fill', circleOptions.fill)
        // .style('filter', 'url(#template-dropshadow)') // TAMS, 버블 차트 그림자 제거 (미사용)
        .style('fill-opacity', circleOptions['fill-opacity'])
        .style('stroke', circleOptions.stroke)
        .style('stroke-width', circleOptions['stroke-width'])

      // Circle에 마우스 이벤트 추가
      if (circleOptions.click) {
        newCircle.on('click', (data) => {
          // const event = d3.event
          // circleOptions.click(event, data)

          // TAMS
          // 국가 코드 포맷에 따라 저장
          const countryCode = target
          const countryData = this.getCountryCoordinates(vueInstance, countryCode)
          // Path 엘리먼트 저장
          const self = vueInstance.$data.svg.select(`[itemid=path_country_${countryCode}]`)
          // 센터 좌표 구하기
          const centerCoordinate = this.getCenterCoordinates(vueInstance, countryCode)
          // D3 Event 저장
          const event = d3.event
          // D3 지도 데이터에 파라미터 설정
          countryData.coordinate = centerCoordinate
          // 지도 클릭 이벤트 연결
          circleOptions.click(self, event, countryData)
        })
      }

      if (circleOptions.mouseout) {
        newCircle.on('mouseout', (data) => {
          // const event = d3.event
          // circleOptions.mouseout(event, data)

          // TAMS
          // 국가 코드 포맷에 따라 저장
          const countryCode = target
          const countryData = this.getCountryCoordinates(vueInstance, countryCode)
          const self = vueInstance.$data.svg.select(`[itemid=path_country_${countryCode}]`)
          // 센터 좌표 구하기
          const centerCoordinate = this.getCenterCoordinates(vueInstance, countryCode)
          // D3 Event 저장
          const event = d3.event
          // D3 지도 데이터에 파라미터 설정
          countryData.coordinate = centerCoordinate
          circleOptions.mouseout(self, event, countryData)
        })
      }

      if (circleOptions.mouseover) {
        newCircle.on('mouseover', (data) => {
          // const event = d3.event
          // circleOptions.mouseover(event, data)

          // TAMS
          // 국가 코드 포맷에 따라 저장
          const countryCode = target
          const countryData = this.getCountryCoordinates(vueInstance, countryCode)
          const self = vueInstance.$data.svg.select(`[itemid=path_country_${countryCode}]`)
          // 센터 좌표 구하기
          const centerCoordinate = this.getCenterCoordinates(vueInstance, countryCode)
          // D3 Event 저장
          const event = d3.event
          // D3 지도 데이터에 파라미터 설정
          countryData.coordinate = centerCoordinate
          circleOptions.mouseover(self, event, countryData)
        })
      }

      if (circleOptions.mousemove) {
        newCircle.on('mousemove', (data) => {
          // const event = d3.event
          // circleOptions.mousemove(event, data)

          // TAMS
          // 국가 코드 포맷에 따라 저장
          const countryCode = target
          const countryData = this.getCountryCoordinates(vueInstance, countryCode)
          const self = vueInstance.$data.svg.select(`[itemid=path_country_${countryCode}]`)
          // 센터 좌표 구하기
          const centerCoordinate = this.getCenterCoordinates(vueInstance, countryCode)
          // D3 Event 저장
          const event = d3.event
          // D3 지도 데이터에 파라미터 설정
          countryData.coordinate = centerCoordinate
          circleOptions.mousemove(self, event, countryData)
        })
      }

      const textData = circleOptions.title
      const fontSize = Math.min(circleOptions.size * 2, (circleOptions.size * 2 - 10) / textData.length)

      newCircleGroup.append('text')
        .attr('x', center.cx + circleOptions.offsetX)
        .attr('y', center.cy + circleOptions.offsetY)
        .attr('dy', '0.35em')
        .style('text-anchor', 'middle')
        .style('font-weight', circleOptions.text['font-weight'])
        .style('font-size', fontSize)
        .style('fill', circleOptions.text.fill)
        .text(textData)

      return newCircleItemId
    } else {
      // 변경하고자 하는 도형의 itemId가 전달되었다면 속성 변경
      const targetCircle = circleGroup.select(`[itemid=${circleItemId}]`)

      // 대상 도형이 있는지 확인
      if (targetCircle.empty()) {
        console.error('There is no circular element with the itemid property passed in.') // 전달된 itemid 속성을 가지는 원형 요소가 없습니다.

        return
      }

      const circleSize = (!options.size) ? targetCircle.attr('r') : options.size

      // 도형 속성 변경
      targetCircle.attr('cx', ((!options.offsetX) ? 0 : options.offsetX) + center.cx)
        .attr('cy', ((!options.offsetY) ? 0 : options.offsetY) + center.cy)
        .attr('r', circleSize)
        .style('fill', (!options.fill) ? targetCircle.style('fill') : options.fill)
        .style('fill-opacity', (!options['fill-opacity']) ? targetCircle.style('fill-opacity') : options['fill-opacity'])
        .style('stroke', (!options.stroke) ? targetCircle.style('stroke') : options.stroke)
        .style('stroke-width', (!options['stroke-width']) ? targetCircle.style('stroke-width') : options['stroke-width'])

      // 도형의 텍스트 속성 변경
      const textOption = options.text || {}
      const targetText = targetCircle.select(function () {
        return this.parentNode
      }).select('text')

      const circleTitle = (!options.title) ? targetText.text() : options.title

      targetText.attr('x', Number((!options.offsetX) ? 0 : options.offsetX) + center.cx)
        .attr('y', Number((!options.offsetY) ? 0 : options.offsetY) + center.cy)
        .attr('dy', '0.35em')
        .style('font-size', (!options.size) ? targetText.style('size') : Math.min(options.size * 2, (options.size * 2 - 10) / circleTitle.length))
        .style('font-weight', Number((!textOption['font-weight']) ? targetText.style('font-weight') : textOption['font-weight']))
        .style('fill', (!textOption.fill) ? targetText.style('fill') : textOption.fill)
        .text(circleTitle)

      return circleItemId
    }
  }
  /*
  Method - drawImage

  전달된 ImagePath의 이미지를 Target 좌표에 렌더링합니다. 동일한 위치에 이미지를 변경하고자 하는 경우, 리턴된 이미지의 name과 변경할 이미지의 경로를
  전달합니다. 반드시 이미지 요소의 Name 값을 전달해주세요.
   */
  d3Figure.drawImage = function (vueInstance, target, imagePath = '', options = {}, imageItemId = '') {
    // 이미지가 그려질 좌표 구하기
    const center = this.getCenterCoordinates(vueInstance, target)
    const imageOptions = {...vueInstance.$data.defaultOptions.image, ...options}

    // g_path_figure 아래에 g_figure_image 요소 검색
    let imageGroup = vueInstance.$data.svg.select('[itemid=g_path_figure]')
      .select('[itemid=g_figure_image]')

    // Image 그룹이 없다면 g_figure_image 그룹 생성
    if (imageGroup.empty()) {
      imageGroup = vueInstance.$data.svg.select('[itemid=g_path_figure]').append('g')
        .attr('itemid', 'g_figure_image')
    }

    // 이미지를 추가할 것인지, 업데이트인지 확인
    if (imageItemId === '') {
      // 그룹 내부에 이미지 태그가 몇 개 있는지 확인
      const imageCount = imageGroup.selectAll('image').size()

      // 새로운 Name 생성
      const newImageItemId = `image_itemid_${target.toLowerCase()}_${imageCount}`

      const newImage = imageGroup.append('image')
        .attr('itemid', newImageItemId)

      // 새로운 이미지 생성
      if (imageOptions.data.length > 0) {
        newImage.data(imageOptions.data)
      }

      // 새로운 이미지 속성 추가
      newImage.attr('xlink:href', imagePath)
        .attr('x', center.cx + imageOptions.offsetX)
        .attr('y', center.cy + imageOptions.offsetY)
        .attr('offsetX', imageOptions.offsetX)
        .attr('offsetY', imageOptions.offsetY)
        .attr('width', imageOptions.width)
        .attr('height', imageOptions.height)

      // 새로운 이미지의 마우스 이벤트 연결
      if (imageOptions.click) {
        newImage.on('click', (data) => {
          const event = d3.event
          imageOptions.click(event, data)
        })
      }

      if (imageOptions.mouseout) {
        newImage.on('mouseout', (data) => {
          const event = d3.event
          imageOptions.mouseout(event, data)
        })
      }

      if (imageOptions.mouseover) {
        newImage.on('mouseover', (data) => {
          const event = d3.event
          imageOptions.mouseover(event, data)
        })
      }

      if (imageOptions.mousemove) {
        newImage.on('mousemove', (data) => {
          const event = d3.event
          imageOptions.mousemove(event, data)
        })
      }

      // 생성된 이미지의 Name 리턴
      return newImageItemId
    } else {
      // 변경하고자 하는 이미지의 Name이 전달되었다면 속성 변경
      const targetImage = imageGroup.select(`[itemid=${imageItemId}]`)

      // 이미지가 있는지 확인
      if (targetImage.empty()) {
        console.error('No image element with passed itemid attribute.') // 전달된 itemid 속성을 가지는 이미지 요소가 없습니다.

        return
      }

      // 이미지 변경
      if (imagePath !== '') {
        targetImage.attr('xlink:href', imagePath)
      }

      // 데이터 변경
      if (imageOptions.data.length > 0) {
        targetImage.data(options.data)
      }

      // 이미지 속성 변경
      targetImage.attr('x', Number((!options.offsetX) ? targetImage.attr('offsetX') : options.offsetX) + center.cx)
        .attr('y', Number((!options.offsetY) ? targetImage.attr('offsetY') : options.offsetY) + center.cy)
        .attr('width', Number((!options.width) ? targetImage.attr('width') : options.width))
        .attr('height', Number((!options.height) ? targetImage.attr('height') : options.height))

      return imageItemId
    }
  }
  /*
  Method - selectCountryByCode

  전달된 국가코드를 맵 데이터에서 검색하여 국가를 선택합니다.
   */
  d3Figure.selectCountryByCode = function (vueInstance, code) {
    // 0. SVG 엘리먼트에서 세계지도에 해당하는 SVG Group 선택
    const group = vueInstance.$data.svg.select('[itemid=g_path_worldmap]')

    // 1. 그룹 데이터 검색 (Path)
    const selectCountry = group.selectAll('path').filter(function (data) {
      if (data.properties[vueInstance.$data.format || 'iso_a2'] === code) {
        return data
      }
    })

    // 2. 검색된 데이터 확인. 있으면 Click Trigger 발생, 없으면 가장 마지막에 클릭된 요소 해제.
    if (selectCountry._groups[0].length > 0) {
      selectCountry.dispatch('click')
      return true
    } else {
      // 마지막에 선택된 Path 정보 가져오기
      let beforeSelected = vueInstance.$data.innerMemory.worldmap.beforeSelected

      // 선택된 정보가 있는 경우에만 동작
      if (beforeSelected) {
        // 릴리즈 Flag 설정
        beforeSelected.event.release = true

        // 클릭 핸들러 호출
        vueInstance.$data.options.click(beforeSelected.self, beforeSelected.event, beforeSelected.data)

        // InnerMemory 정리
        vueInstance.$data.innerMemory.worldmap = {}
      }
    }

    return false
  }
  /*
    Method - getSvgMousePosition

    전달된 국가코드를 맵 데이터에서 검색하여 국가를 선택합니다.
  */
  d3Figure.getSvgMousePosition = function (vueInstance) {
    // SVG 노드에서 마우스 좌표 얻어오기
    const position = d3.mouse(vueInstance.$data.svg.node())

    return position
  }
  /*
    Method - isCountryPathByCode

    전달된 국가코드가 Path에 반영되어있는지 확인합니다.
  */
  d3Figure.isCountryPathByCode = function (vueInstance, code) {
    // 0. SVG 엘리먼트에서 세계지도에 해당하는 SVG Group 선택
    const group = vueInstance.$data.svg.select('[itemid=g_path_worldmap]')

    // 1. 그룹 데이터 검색 (Path)
    const selectCountry = group.selectAll('path').filter(function (data) {
      if (data.properties[vueInstance.$data.format || 'iso_a2'] === code) {
        return data
      }
    })

    // 2. 검색된 국가가 없으면 return False
    if (selectCountry._groups[0].length === 0) {
      return false
    }

    return true
  }

  return d3Figure
})()

export default d3Figure
